import React, { useCallback, useEffect, useState } from 'react';

import { PausaNasajonCilia, getPausaNasajon, setPausaNasajon } from '../../../api/orcamentoCiliaApi';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import { notify } from '../../../shared/feedback/notify';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/feedback/aviso-de-tela.css';
import '../../../shared/ui/badge-status.css';
import '../../admin-sistema/pages/css/adminSistema.css';

/**
 * A pausa das rotinas do módulo de Requisições que chamam o Nasajon (transversal/at-99, ADR 0012).
 *
 * ## O que ela para, e o que NÃO para
 *
 * Para as duas rotinas automáticas (o aviso de peça pendente de verificação e a sincronização do
 * desfecho das requisições). As ações da tela — importar, concluir, reverificar — seguem
 * funcionando: barrá-las impediria a orçamentista de trabalhar (decisão do Victor, 26/10/2026).
 *
 * ## É uma pausa DESTE sistema
 *
 * O Dashboard de Compras tem a sua, em Administração → Compras. Um botão por sistema, cada um
 * gravado no próprio banco.
 *
 * ## Sem atualização otimista, e falha de carga nunca vira "Ativo"
 *
 * Molde do `SincronizacaoComprasSection` (at-108): o botão só muda depois que o servidor confirma, e
 * sem o estado a tela diz que não conseguiu verificar — exibir "Ativo" por padrão faria alguém
 * concluir que nada está pausado (regra da at-60).
 */

function formatarDataHora(iso: string | null): string | null {
  if (!iso) return null;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  return data.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

const PausaNasajonCard: React.FC = () => {
  const [estado, setEstado] = useState<PausaNasajonCilia | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [confirmarPausa, setConfirmarPausa] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setEstado(await getPausaNasajon());
      setErro(false);
    } catch (error) {
      setEstado(null);
      setErro(true);
      notify.errorFrom(
        error,
        'Não foi possível verificar se as rotinas do Nasajon estão pausadas. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const alternar = useCallback(async (pausado: boolean) => {
    setSalvando(true);
    try {
      setEstado(await setPausaNasajon(pausado));
      notify.success(
        pausado
          ? 'Rotinas pausadas. Elas não chamam o Nasajon até você retomar.'
          : 'Rotinas retomadas. Elas voltam a rodar no próximo ciclo.',
      );
    } catch {
      notify.error(
        pausado
          ? 'Não foi possível pausar. As rotinas continuam ativas.'
          : 'Não foi possível retomar. As rotinas continuam pausadas.',
      );
    } finally {
      setSalvando(false);
    }
  }, []);

  const em = formatarDataHora(estado?.em ?? null);
  const autoria = `${em ? ` em ${em}` : ''}${estado?.por ? ` por ${estado.por}` : ''}`;

  return (
    <section className="admin-card" style={{ width: '100%' }}>
      <h2 className="admin-card-title">Chamadas ao Nasajon</h2>

      <p className="admin-hint">
        Duas rotinas deste módulo consultam o Nasajon sozinhas, a cada 10 minutos: o{' '}
        <strong>aviso de peças pendentes de verificação</strong> e a{' '}
        <strong>sincronização do desfecho das requisições</strong> (aprovada, recusada). Pausar
        interrompe só essas duas. Importar, concluir e reverificar continuam funcionando, porque
        são ações de quem está trabalhando na tela.
      </p>
      <p className="admin-hint">
        Esta pausa vale só para Requisições. O Dashboard de Compras tem a sua, em Administração do
        Sistema → Compras: para parar todas as chamadas ao Nasajon, pause nas duas.
      </p>

      {erro && (
        <p className="erro-de-tela">
          Não foi possível verificar o estado das rotinas. Isto não significa que elas estejam
          ativas nem pausadas — significa que não conseguimos perguntar.
        </p>
      )}

      {carregando && <p className="admin-hint">Carregando...</p>}

      {estado && (
        <>
          <div className="admin-input-group">
            <span className={`cilia-badge ${estado.pausado ? 'badge-cinza' : 'badge-verde'}`}>
              {estado.pausado ? 'Pausadas' : 'Ativas'}
            </span>
            <span className="admin-texto-secundario">
              {estado.pausado
                ? `Pausadas${autoria}.`
                : estado.em
                  ? `Retomadas${autoria}.`
                  : 'Nunca foram pausadas neste sistema.'}
            </span>
            <button
              type="button"
              className={`admin-btn ${estado.pausado ? 'admin-btn-primary' : 'admin-btn-danger-outline'}`}
              disabled={salvando}
              onClick={() => {
                if (estado.pausado) void alternar(false);
                else setConfirmarPausa(true);
              }}
            >
              {salvando ? 'Salvando...' : estado.pausado ? 'Retomar' : 'Pausar'}
            </button>
          </div>

          {estado.pausado && (
            <p className="aviso-de-tela">
              Enquanto durar a pausa, o aviso de peças pendentes não sai e o desfecho das
              requisições no Nasajon não é atualizado aqui. Nada é apagado: ao retomar, o próximo
              ciclo volta a consultar tudo o que estiver em aberto.
            </p>
          )}
        </>
      )}

      {confirmarPausa && (
        <ConfirmModal
          isOpen
          title="Pausar as rotinas do Nasajon?"
          message={
            'O aviso de peças pendentes de verificação e a sincronização do desfecho das ' +
            'requisições deixam de consultar o Nasajon até você retomar. Importar, concluir e ' +
            'reverificar continuam funcionando. A pausa do Dashboard de Compras é outra e não muda.'
          }
          confirmLabel="Pausar"
          onConfirm={() => {
            setConfirmarPausa(false);
            void alternar(true);
          }}
          onCancel={() => setConfirmarPausa(false)}
        />
      )}
    </section>
  );
};

export default PausaNasajonCard;
